import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link as MuiLink,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';

import { DEPARTMENT_CHOICES } from '../../utils/employeeConstants';
import { generateAttendanceReport, getAllEmployees } from '../../services/api';
import { toast } from '../toast';
import DateRangeFilter from '../DateRangeFilter';

const DEFAULT_DATE_RANGE = { startDate: null, endDate: null, preset: 'All' };

const buildDepartmentOptions = (departments) => {
  const options =
    departments && departments.length > 0
      ? departments.map((dept) => ({ value: dept, label: dept }))
      : DEPARTMENT_CHOICES.map((dept) => ({ value: dept, label: dept }));
  return [{ value: 'ALL', label: 'All Departments' }, ...options];
};

const AttendanceReportDialog = ({ open, onClose, departmentOptions: departmentOptionsProp }) => {
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [employees, setEmployees] = useState([]);
  const [excludedIds, setExcludedIds] = useState(() => new Set());
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const departmentOptions = useMemo(() => buildDepartmentOptions(departmentOptionsProp), [departmentOptionsProp]);

  const selectedDepartmentOption = useMemo(
    () => departmentOptions.find((opt) => opt.value === departmentFilter) || null,
    [departmentOptions, departmentFilter]
  );

  useEffect(() => {
    if (!open) {
      setDepartmentFilter('');
      setDateRange(DEFAULT_DATE_RANGE);
      setEmployees([]);
      setExcludedIds(new Set());
      setIsLoadingEmployees(false);
      setIsSubmitting(false);
      setErrorMessage('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    if (!departmentFilter) {
      setEmployees([]);
      setExcludedIds(new Set());
      return undefined;
    }

    if (departmentFilter === 'ALL') {
      setEmployees([]);
      setExcludedIds(new Set());
      return undefined;
    }

    let cancelled = false;

    const fetchEmployeesByDepartment = async () => {
      setIsLoadingEmployees(true);
      setErrorMessage('');
      try {
        const response = await getAllEmployees({
          department: departmentFilter,
          fields: 'id,employee_id,full_name,designation,department',
        });
        if (cancelled) return;
        const results = response?.data?.results || [];
        setEmployees(results);
        setExcludedIds(new Set());
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading employees for report:', error);
        setEmployees([]);
        setErrorMessage(error?.response?.data?.message || 'Failed to load employees for the selected department.');
      } finally {
        if (!cancelled) {
          setIsLoadingEmployees(false);
        }
      }
    };

    fetchEmployeesByDepartment();

    return () => {
      cancelled = true;
    };
  }, [open, departmentFilter]);

  const includedEmployees = useMemo(
    () => employees.filter((emp) => !excludedIds.has(emp.id)),
    [employees, excludedIds]
  );

  const hasRemovedEmployees = excludedIds.size > 0;

  const handleDepartmentChange = useCallback((_event, newValue) => {
    setDepartmentFilter(newValue?.value || '');
  }, []);

  const handleDateRangeChange = useCallback((newDateRange) => {
    setDateRange(newDateRange);
  }, []);

  const isDepartmentSelected = Boolean(departmentFilter);
  const isAllDepartmentsSelected = departmentFilter === 'ALL';

  const handleRemoveEmployee = useCallback((employeeId) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      next.add(employeeId);
      return next;
    });
  }, []);

  const handleResetEmployees = useCallback(() => {
    setExcludedIds(new Set());
  }, []);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    onClose();
  }, [isSubmitting, onClose]);

  const handleGenerate = useCallback(async () => {
    if (!isAllDepartmentsSelected && includedEmployees.length === 0) {
      toast.error('Select at least one employee to generate the report.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        type: 'excel',
      };

      if (!isAllDepartmentsSelected) {
        payload.employees = includedEmployees.map((emp) => emp.id);
      }

      const response = await generateAttendanceReport(payload);

      const { message, background_task_id: backgroundTaskId } = response?.data || {};
      const successText = message || 'Attendance report generation has been queued.';

      toast.success(
        <>
          {`${successText}${backgroundTaskId ? ` Background task ID: ${backgroundTaskId}.` : ''}`}
          <br />
          <MuiLink href="/background-tasks" underline="always" sx={{ fontWeight: 700, color: '#20721C' }}>
            View Tasks
          </MuiLink>
        </>
      );
      onClose();
    } catch (error) {
      console.error('Error queuing attendance report:', error);
      const message = error?.response?.data?.message;
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [includedEmployees, dateRange.startDate, dateRange.endDate, isAllDepartmentsSelected, onClose]);

  const canGenerate =
    isDepartmentSelected && (isAllDepartmentsSelected || includedEmployees.length > 0) && !isSubmitting;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
              }}
            >
              <AssessmentRoundedIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                Generate Attendance Report
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Filter employees by department, set a date range, and export to Excel.
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Close">
            <span>
              <IconButton onClick={handleClose} disabled={isSubmitting} size="small">
                <CloseRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1.25}>
              <FilterAltRoundedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Filters
              </Typography>
            </Box>
            <Stack spacing={2}>
              <Box sx={{ width: '100%', maxWidth: { md: 360 } }}>
                <Autocomplete
                  size="small"
                  fullWidth
                  value={selectedDepartmentOption}
                  onChange={handleDepartmentChange}
                  options={departmentOptions}
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                  renderInput={(params) => <TextField {...params} label="Department" placeholder="Select department" />}
                  clearOnEscape
                  disabled={isSubmitting}
                />
              </Box>
              <DateRangeFilter
                onDateRangeChange={handleDateRangeChange}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                selectedPreset={dateRange.preset}
                label="Report Date Range"
                sx={{
                  height: 'auto',
                  flexWrap: 'wrap',
                  gap: 2,
                  '& > :not(style) ~ :not(style)': { ml: 0 },
                  '& > .MuiBox-root:first-of-type': {
                    width: 'auto',
                    flex: '0 0 auto',
                    minWidth: 220,
                    maxWidth: { xs: '100%', sm: 280 },
                  },
                  '& .MuiTextField-root': {
                    width: { xs: '100%', sm: 220, md: 240 },
                    minWidth: 0,
                  },
                }}
              />
            </Stack>
          </Box>

          <Divider flexItem />

          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1.25}>
              <Box display="flex" alignItems="center" gap={1}>
                <GroupsRoundedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Employees in Report
                </Typography>
                <Chip
                  size="small"
                  label={
                    isAllDepartmentsSelected
                      ? 'All employees'
                      : isDepartmentSelected
                      ? `${includedEmployees.length} selected${hasRemovedEmployees ? ` of ${employees.length}` : ''}`
                      : 'No department selected'
                  }
                  color={isDepartmentSelected && includedEmployees.length > 0 ? 'primary' : 'default'}
                  variant={isDepartmentSelected && includedEmployees.length > 0 ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 500 }}
                />
              </Box>
              {hasRemovedEmployees && (
                <Button
                  size="small"
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={handleResetEmployees}
                  disabled={isSubmitting}
                  sx={{ textTransform: 'none' }}
                >
                  Reset removed
                </Button>
              )}
            </Box>

            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                {errorMessage}
              </Alert>
            )}

            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.6),
                minHeight: 240,
                maxHeight: 380,
                overflow: 'auto',
              }}
            >
              {!isDepartmentSelected ? (
                <EmptyState
                  title="Select a department to load employees"
                  caption="Pick a department above to populate the report with its employees."
                />
              ) : isAllDepartmentsSelected ? (
                <EmptyState
                  title="All departments selected"
                  caption="All employees across all departments will be included in the report."
                />
              ) : isLoadingEmployees ? (
                <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column" gap={1.5} py={6}>
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    Loading employees in {departmentFilter}...
                  </Typography>
                </Box>
              ) : employees.length === 0 ? (
                <EmptyState
                  title={`No employees found in ${departmentFilter}`}
                  caption="Try a different department or check that employees exist in this department."
                />
              ) : includedEmployees.length === 0 ? (
                <EmptyState
                  title="All employees have been removed"
                  caption="Use Reset removed to add them back, or pick a different department."
                />
              ) : (
                <List disablePadding>
                  {includedEmployees.map((employee, index) => (
                    <React.Fragment key={employee.id}>
                      {index > 0 && <Divider component="li" />}
                      <ListItem
                        secondaryAction={
                          <Tooltip title="Remove from report">
                            <span>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleRemoveEmployee(employee.id)}
                                disabled={isSubmitting}
                                sx={{
                                  color: 'text.secondary',
                                  '&:hover': { color: 'error.main' },
                                }}
                              >
                                <CloseRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        }
                        sx={{ py: 1.25 }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                              color: 'primary.main',
                              fontWeight: 600,
                            }}
                          >
                            {(employee.full_name || '?').charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {employee.full_name}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {employee.employee_id ? `#${employee.employee_id}` : `ID: ${employee.id}`}
                              {employee.designation ? ` · ${employee.designation}` : ''}
                              {employee.department ? ` · ${employee.department}` : ''}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={!canGenerate}
          startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <AssessmentRoundedIcon />}
          sx={{ textTransform: 'none', minWidth: 180 }}
        >
          {isSubmitting
            ? 'Generating...'
            : `Generate Report${includedEmployees.length ? ` (${includedEmployees.length})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EmptyState = ({ title, caption }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexDirection="column"
    textAlign="center"
    px={3}
    py={6}
    gap={0.75}
  >
    <Typography variant="subtitle2" color="text.primary">
      {title}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 360 }}>
        {caption}
      </Typography>
    )}
  </Box>
);

EmptyState.propTypes = {
  title: PropTypes.string.isRequired,
  caption: PropTypes.string,
};

EmptyState.defaultProps = {
  caption: '',
};

AttendanceReportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  departmentOptions: PropTypes.arrayOf(PropTypes.string),
};

AttendanceReportDialog.defaultProps = {
  departmentOptions: undefined,
};

export default AttendanceReportDialog;
